import React from "react";

import { useTheme } from "@/components/ui/theme-provider";
import { isOplotFlavor } from "@/env/env";
import { cn } from "@/lib/utils";

import authIllustrationDark from "./login-illustration-nodeshift-dark.svg";
import authIllustrationLight from "./login-illustration-nodeshift-light.svg";
import { shouldShowAuthIllustration } from "./utils";

export const AuthLayout = ({
  children,
}: React.PropsWithChildren): React.ReactElement => {
  const { hasCustomBranding, isBrandingResolved } = useTheme();

  // NOTE: NSAI-6144 — the three inputs and why each one gates the artwork live
  // on `shouldShowAuthIllustration`'s props. The flavor one is the fix: the
  // Oplot build used to render the NodeShift astronaut next to the Oplot logo on
  // sign in / sign up / SSO, which all come through this layout. With the column
  // gone the form keeps the full width by itself — `basis-full` below is
  // unconditional and only gains the `lg:basis-1/2` split when the artwork shows.
  // Unconditional here, unlike `CenterCardLayout`: this layout has no neutral
  // asset pair, so there is no screen it could keep the artwork for.
  const isIllustrationVisible = shouldShowAuthIllustration({
    isFlavorGated: isOplotFlavor(),
    isBrandingResolved,
    hasCustomBranding,
  });

  return (
    // NOTE: NSAI-5912 — same shell defect as CenterCardLayout, which carries
    // the full reasoning: `100vh` is the large viewport and ignores mobile
    // browser chrome, and a fixed height shorter than its own content clips the
    // form's top out of reach once the keyboard shrinks the viewport. Sign in
    // and sign up own a password field too, so they need it for the same
    // reason. Fixed here rather than left for later because it is the same
    // one-line defect in the sibling shell.
    <div
      data-testid="auth-layout"
      className="min-h-dvh w-full flex bg-background-secondary"
    >
      <div
        className={cn("basis-full", isIllustrationVisible && "lg:basis-1/2")}
      >
        <div className="flex flex-col justify-center max-w-lg mx-auto h-full p-4">
          {children}
        </div>
      </div>
      {isIllustrationVisible && (
        <div
          data-testid="auth-layout_illustration"
          className="basis-1/2 hidden lg:block bg-cover bg-center [background-image:var(--auth-illustration-light)] dark:[background-image:var(--auth-illustration-dark)]"
          style={
            {
              "--auth-illustration-light": `url("${authIllustrationLight}")`,
              "--auth-illustration-dark": `url("${authIllustrationDark}")`,
              // NOTE: We need to cast this because the style object is not typed to pass custom properties
            } as React.CSSProperties
          }
        />
      )}
    </div>
  );
};
